import {
  ArrowLeftOutlined,
  CalendarOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Empty, Flex, Grid, Modal, Spin, Typography } from "antd";
import { AxiosError } from "axios";
import {
  acceptRequestByNumber,
  cancelRequestByNumber,
  getRequestById,
} from "entities/requests";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { notification } from "shared/lib/antd-global";
import { getGlobalCurrency } from "shared/lib/utils";
import ROUTES from "shared/routes";
import styles from "./index.module.scss";

export const RequestPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { requestNumber } = useParams();
  const [opened, setOpened] = useState(false);
  const screens = Grid.useBreakpoint();
  const isMobile = screens.xs === true;

  const {
    data: request,
    isLoading: isLoadingRequest,
    isError: isErrorRequest,
    error,
  } = useQuery({
    queryKey: ["request", requestNumber],
    queryFn: () => getRequestById(requestNumber!),
    enabled: !!requestNumber,
  });

  const { mutate: acceptRequest, isPending: isAcceptRequestPending } =
    useMutation({
      mutationFn: (number: string) => acceptRequestByNumber(number),
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: ["request"] });
        notification.success({ title: t("requests.accept_success") });
      },
      onError(error: AxiosError<{ message: string }>) {
        notification.error({
          title: error.response?.data?.message || t("requests.something_wrong"),
        });
      },
    });

  const { mutate: cancelRequest, isPending: isCancelRequestPending } =
    useMutation({
      mutationFn: (number: string) => cancelRequestByNumber(number),
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: ["request"] });
        notification.success({ title: t("requests.cancel_success") });
      },
      onError(error: AxiosError<{ message: string }>) {
        notification.error({
          title: error.response?.data?.message || t("requests.something_wrong"),
        });
      },
    });

  useEffect(() => {
    if (
      request?.status?.code === "RECRUITMENT" &&
      requestNumber &&
      !request?.is_responded
    ) {
      openAlert();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.status?.code, requestNumber]);

  const openAlert = () => {
    setOpened(true);
  };

  if (isLoadingRequest || isAcceptRequestPending) {
    return (
      <div className={styles.wrapperLoader}>
        <Spin />
      </div>
    );
  }

  if (isErrorRequest || !request || !requestNumber) {
    return (
      <div className={styles.wrapper}>
        <Typography.Title level={3}>
          <Button
            type="link"
            size="large"
            icon={
              <ArrowLeftOutlined style={{ fontSize: "20px", marginRight: 8 }} />
            }
            onClick={() => navigate(ROUTES.REQUESTS)}
          ></Button>
          {t("requests.title")}
        </Typography.Title>
        <Empty
          description={
            <Typography.Text>
              {isErrorRequest
                ? (error as any)?.response?.data?.message ||
                  t("requests.document_load_error")
                : t("requests.document_not_found")}
            </Typography.Text>
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Typography.Title level={3}>
        <Button
          type="link"
          size="large"
          icon={
            <ArrowLeftOutlined style={{ fontSize: "20px", marginRight: 8 }} />
          }
          onClick={() => navigate(ROUTES.REQUESTS)}
        ></Button>
        {t("requests.request_number")} {request?.number}
        <br />
        <div
          style={{
            fontWeight: 400,
            fontSize: 14,
            padding: "6px 12px",
            borderRadius: 8,
            display: "inline-block",
            border: "1px solid var(--color-grey-light)",
          }}
        >
          {t("requests.status_label")} {request.status?.label}
        </div>
      </Typography.Title>
      <Typography.Title level={4}>{request.name}</Typography.Title>
      <div className={styles.info}>
        <div className={styles.infoRow}>
          <DollarOutlined className={styles.icon} />

          <div>
            <div className={styles.blockTitle}>{t("requests.budget")}</div>
            <div className={styles.blockText}>
              {getGlobalCurrency(request.total_net)}
            </div>
          </div>
        </div>
        <div className={styles.infoRow}>
          <CalendarOutlined className={styles.icon} />

          <div>
            <div className={styles.blockTitle}>{t("requests.start_date")}</div>
            <div className={styles.blockText}>
              {moment(request.date_start).format("DD.MM.YYYY")}{" "}
              {request.time_start}
            </div>
          </div>
          <div>
            <div className={styles.blockTitle}>{t("requests.end_date")}</div>
            <div className={styles.blockText}>
              {moment(request.date_finish).format("DD.MM.YYYY")}{" "}
              {request.time_finish}
            </div>
          </div>
        </div>
      </div>
      <Typography.Title level={5}>{t("requests.customer")}</Typography.Title>
      <Typography.Text style={{ color: "var(--color-grey)" }}>
        {request.company?.name}
      </Typography.Text>

      <Typography.Title level={5}>{t("requests.description")}</Typography.Title>
      <Typography.Text style={{ color: "var(--color-grey)" }}>
        {request.description}
      </Typography.Text>

      <Typography.Title level={5}>
        {t("requests.payment_info")}
      </Typography.Title>
      <Typography.Text style={{ color: "var(--color-grey)" }}>
        {request.payment_info}
      </Typography.Text>

      <Typography.Title level={5}>
        {t("requests.count_executors")}
      </Typography.Title>
      <Typography.Text style={{ color: "var(--color-grey)" }}>
        {request.count_executors}
      </Typography.Text>

      <Typography.Title level={5}>{t("requests.map_link")}</Typography.Title>
      <Typography.Text style={{ color: "var(--color-grey)" }}>
        <Button
          type="link"
          onClick={() => window.open(request.map_url, "_blank")}
          style={{ padding: 0 }}
        >
          {request.map_url}
        </Button>
      </Typography.Text>

      <Typography.Title level={5}>
        {t("requests.services_list")}
      </Typography.Title>
      <ul className={styles.description}>
        {request.services?.map((item, index) => (
          <li key={index}>{item?.fields?.description}</li>
        ))}
      </ul>

      <div className={styles.footer}>
        {request?.status?.code === "RECRUITMENT" && !request?.is_responded && (
          <Button
            block
            type="primary"
            onClick={() => setOpened(true)}
            loading={isCancelRequestPending}
          >
            {t("requests.accept_button")}
          </Button>
        )}
        {(request?.status?.code === "RECRUITMENT" ||
          request?.status?.code === "RECRUITMENT_DONE" ||
          request?.status?.code === "IN_PROGRESS") &&
          request?.is_responded && (
            <Button
              block
              onClick={() => cancelRequest(requestNumber)}
              loading={isCancelRequestPending}
            >
              {t("requests.cancel_response")}
            </Button>
          )}
      </div>

      <Modal
        title={
          <Typography.Title level={3}>
            {t("requests.accept_confirm_title")}
          </Typography.Title>
        }
        open={opened}
        onCancel={() => setOpened(false)}
        footer={
          <Flex gap="small" justify="end">
            <Button onClick={() => setOpened(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              type="primary"
              loading={isAcceptRequestPending}
              onClick={() => {
                setOpened(false);
                acceptRequest(requestNumber);
              }}
            >
              {t("requests.accept_button")}
            </Button>
          </Flex>
        }
        style={isMobile ? { top: "25%" } : {}}
        className={styles.TaskActionModal}
      >
        <Typography.Text>
          {t("requests.accept_confirm_description")}
        </Typography.Text>
      </Modal>
    </div>
  );
};
